/**
 * Sauna Salt landing page (templates/page.sauna-salt.json)
 * - Buy box: purchase type, quantity tiers, subscription plans, live summary
 *   and add to cart through /cart/add.js.
 * - Links to #anchors open the matching <details> (Nutrition Facts, safety FAQ).
 * - Phone sticky bar hides while the buy box or the site footer is on screen.
 * Loaded by every nw-ssl-* section, so it guards against running twice.
 */
(() => {
  if (window.nwSslLanding) return;
  window.nwSslLanding = true;

  const cartEndpoint = (path) => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/?$/, '/') + path.replace(/^\//, '');
  };

  const escapeHtml = (value) =>
    String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  // Replaces [placeholders] in a theme-editor label: fill('[total] per delivery', { total: '$51.98' })
  const fill = (template, values) =>
    String(template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));

  // Same output as snippets/nw-ssl-label.liquid: a trailing arrow goes in an aria-hidden span.
  const labelHtml = (text) => {
    const label = String(text || '').trim();
    const match = label.match(/^([\s\S]*?)\s*([↗↓→↘])$/u);
    return match ? `${escapeHtml(match[1])} <span aria-hidden="true">${match[2]}</span>` : escapeHtml(label);
  };

  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currency || 'USD',
      }).format(cents / 100);
    } catch (_) {
      return '$' + (cents / 100).toFixed(2);
    }
  };

  class SslBuyBox {
    /** @param {HTMLElement} el */
    constructor(el) {
      this.el = el;
      this.config = JSON.parse(el.querySelector('[data-ssl-config]').textContent);
      this.labels = this.config.labels || {};
      this.boxOptions = el.querySelector('[data-ssl-box-options]');
      this.subscription = el.querySelector('[data-ssl-subscription]');
      this.planSelect = el.querySelector('[data-ssl-plan]');
      this.subscriptionQuantity = el.querySelector('[data-ssl-subscription-quantity]');
      this.button = el.querySelector('[data-ssl-add]');
      this.buttonText = el.querySelector('[data-ssl-button-text]');
      this.buttonHtml = this.buttonText ? this.buttonText.innerHTML : '';
      this.subNoteTemplate = this.labels.subscriptionNote || '';
      this.error = el.querySelector('[data-ssl-error]');
      this.busy = false;

      el.addEventListener('change', () => this.update());
      if (this.button) this.button.addEventListener('click', () => this.add());
      this.update();
    }

    money(cents) {
      return formatMoney(cents, this.config.currency);
    }

    isSubscription() {
      const checked = this.el.querySelector('[data-ssl-purchase]:checked');
      return Boolean(checked && checked.value === 'subscription' && (this.config.subscriptionPlanReady || this.config.subscriptionPreview));
    }

    selection() {
      if (this.isSubscription()) {
        const minimum = Number(this.config.subscriptionMinQuantity || this.config.subscriptionQuantity) || 2;
        const requested = this.subscriptionQuantity ? Number(this.subscriptionQuantity.value) : minimum;
        const quantity = Number.isFinite(requested) ? Math.max(minimum, Math.floor(requested)) : minimum;
        if (this.subscriptionQuantity && String(quantity) !== this.subscriptionQuantity.value) {
          this.subscriptionQuantity.value = String(quantity);
        }
        // Preview: the product has no subscription plan yet, so this can be shown but not bought
        if (!this.planSelect || !this.config.subscriptionPlanReady) {
          return {
            quantity,
            unit: Number(this.config.previewUnit) || 0,
            planId: null,
            planName: this.planSelect && this.planSelect.selectedOptions[0] ? this.planSelect.selectedOptions[0].textContent.trim() : '',
            subscribed: true,
            preview: true,
          };
        }
        const option = this.planSelect.selectedOptions[0];
        return {
          quantity,
          unit: Number(option.dataset.price),
          planId: option.value,
          planName: option.textContent.trim(),
          subscribed: true,
          preview: false,
        };
      }
      const box = this.el.querySelector('[data-ssl-box]:checked') || this.el.querySelector('[data-ssl-box]');
      return {
        quantity: box ? Number(box.value) : 1,
        unit: box ? Number(box.dataset.price) : 0,
        planId: null,
        planName: '',
        subscribed: false,
        preview: false,
      };
    }

    setText(selector, text) {
      const node = this.el.querySelector(selector);
      if (node) node.textContent = text;
    }

    update() {
      const { quantity, unit, planName, subscribed, preview } = this.selection();
      const subtotal = unit * quantity;
      const shipping = quantity >= Number(this.config.freeShippingMin) ? 0 : Number(this.config.shippingPrice) || 0;
      const total = subtotal + shipping;
      const word = quantity === 1 ? this.labels.box : this.labels.boxes;
      const sticks = quantity * (Number(this.config.sticks) || 16);

      if (this.boxOptions) this.boxOptions.hidden = subscribed;
      if (this.subscription) this.subscription.hidden = !subscribed;

      const purchase = subscribed ? this.labels.subscribe : this.labels.oneTime;
      const selection = `${quantity} ${word} · ${sticks} ${this.labels.sticks}${planName ? ` · ${planName}` : ''}`;

      this.setText('[data-ssl-unit-price]', this.money(unit));
      this.setText('[data-ssl-selection]', purchase ? `${purchase} · ${selection}` : selection);
      this.setText('[data-ssl-subtotal]', this.money(subtotal));
      this.setText('[data-ssl-shipping]', shipping ? this.money(shipping) : this.labels.free || 'Free');
      this.setText('[data-ssl-total-label]', subscribed ? this.labels.subscriptionTotal : this.labels.total);
      this.setText('[data-ssl-total]', this.money(total));

      const subNote = this.el.querySelector('[data-ssl-sub-note]');
      if (subscribed && subNote && this.subNoteTemplate) {
        const note = fill(this.subNoteTemplate, { quantity, sticks, unit: this.money(unit), total: this.money(total) });
        subNote.innerHTML = escapeHtml(note).replace(/\n/g, '<br>');
      }

      // Button shows the selected product total (or the price per delivery for a subscription);
      // a previewed subscription can't be bought yet, so the button is disabled.
      let buttonTemplate = this.labels.button;
      if (subscribed) buttonTemplate = preview ? this.labels.previewButton : this.labels.subscriptionButton || this.labels.button;
      if (this.config.available && buttonTemplate) {
        this.buttonHtml = labelHtml(fill(buttonTemplate, { total: this.money(subtotal) }));
        if (this.buttonText && !this.busy) this.buttonText.innerHTML = this.buttonHtml;
      }
      if (this.button && !this.busy) this.button.disabled = !this.config.available || preview;
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
    }

    restoreButton(delay) {
      window.setTimeout(() => {
        this.busy = false;
        this.update();
      }, delay);
    }

    async add() {
      const { quantity, planId, preview } = this.selection();
      if (this.busy || !this.config.available || preview) return;
      this.busy = true;

      const item = { id: Number(this.config.variantId), quantity };
      if (planId) item.selling_plan = Number(planId);

      if (this.error) this.error.hidden = true;
      this.button.disabled = true;
      if (this.buttonText) this.buttonText.textContent = this.labels.adding;

      try {
        const response = await fetch(cartEndpoint('cart/add.js'), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [item] }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || this.labels.error);
        }

        if (this.buttonText) this.buttonText.textContent = this.labels.added;

        // Same events the Sauna Salt PDP fires: the header mini-cart listens for these.
        try {
          const cartResponse = await fetch(cartEndpoint('cart.js'), {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
          });
          const cart = await cartResponse.json();
          document.dispatchEvent(
            new CustomEvent('cart:update', { detail: { data: { itemCount: cart.item_count, source: 'nw-ssl-landing' } } })
          );
          document.dispatchEvent(new CustomEvent('cart:updated'));
        } catch (_) {}
        document.dispatchEvent(new CustomEvent('cart:item-added'));

        this.restoreButton(2200);
      } catch (error) {
        console.warn('Sauna Salt add to cart failed:', error);
        this.showError((error && error.message) || this.labels.error);
        this.restoreButton(0);
      }
    }
  }

  const initBuyBoxes = (scope) => {
    scope.querySelectorAll('[data-ssl-buybox]').forEach((el) => {
      if (el.dataset.sslReady) return;
      el.dataset.sslReady = 'true';
      new SslBuyBox(el);
    });
  };

  /* ---------- #anchor links open the matching <details> ---------- */
  const openDetails = (hash) => {
    if (!hash || hash.length < 2) return;
    let target = null;
    try {
      target = document.getElementById(decodeURIComponent(hash.slice(1)));
    } catch (_) {}
    if (target && target.tagName === 'DETAILS') target.open = true;
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest('.nw-ssl a[href^="#"]');
    if (link) openDetails(link.getAttribute('href'));
  });

  /* ---------- Phone sticky bar ---------- */
  const initMobileBar = () => {
    const bar = document.querySelector('[data-ssl-mobile-bar]');
    if (!bar || bar.dataset.sslReady || !('IntersectionObserver' in window)) return;
    bar.dataset.sslReady = 'true';

    const targets = [
      document.getElementById(bar.dataset.sslTarget || 'shop'),
      document.querySelector('.shopify-section-group-footer-group') || document.querySelector('footer'),
    ].filter(Boolean);
    const onScreen = new Set();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) onScreen.add(entry.target);
          else onScreen.delete(entry.target);
        });
        bar.classList.toggle('is-hidden', onScreen.size > 0);
      },
      { threshold: 0.12 }
    );
    targets.forEach((target) => observer.observe(target));
  };

  /* ---------- Autoplaying videos stay still for visitors who prefer reduced motion ---------- */
  const initVideos = (scope) => {
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    scope.querySelectorAll('.nw-ssl video[autoplay]').forEach((video) => {
      video.removeAttribute('autoplay');
      video.pause();
    });
  };

  const init = (scope) => {
    initBuyBoxes(scope);
    initMobileBar();
    initVideos(scope);
  };

  init(document);
  openDetails(window.location.hash);

  // Theme editor: re-run when a section is added or its settings change.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
